import type { Member } from '@/core/data/members'
import { memberDisplayName } from '@/core/lib/orgMembers'
import type { ViewConfig } from '@/core/views/viewConfig'
import type { DashboardCoaching } from '@/core/types'

export type MemberViewFilter = { excludedRolle: string[]; excludedStatus: string[] }
export const EMPTY_MEMBER_VIEW_FILTER: MemberViewFilter = { excludedRolle: [], excludedStatus: [] }

const ROLLE_KEY = { owner: 'owner', coach: 'coach' } as const
const STATUS_KEY = { aktiv: 'aktiv', ausstehend: 'ausstehend' } as const

function rolleOf(m: Member): string {

  return m.isOwner ? ROLLE_KEY.owner : ROLLE_KEY.coach
}
function statusOf(m: Member): string {
  return m.authUserId === null ? STATUS_KEY.ausstehend : STATUS_KEY.aktiv
}

export function excludedByCategoryFromMemberFilter(filter: MemberViewFilter): Record<string, string[]> {
  return { rolle: filter.excludedRolle, status: filter.excludedStatus }
}

function metaParts(m: Member): (string | null)[] {
  const roles = [m.isOwner ? "Owner" : null, m.isCoach ? "Coach" : null].filter((x): x is NonNullable<typeof x> => !!x)
  return [m.email, ...roles, m.authUserId === null ? "ausstehend" : null]
}

export function coachingCountsByCoachId(coachings: readonly DashboardCoaching[]): Map<string, number> {
  const counts = new Map<string, number>()
  for (const c of coachings) {
    for (const id of new Set([c.coachId, c.secondCoachId])) {
      if (id) counts.set(id, (counts.get(id) ?? 0) + 1)
    }
  }
  return counts
}

export function memberMetaWithCoachings(m: Member, coachingCounts: ReadonlyMap<string, number>): string {
  const n = coachingCounts.get(m.userId) ?? 0
  return [...metaParts(m), n > 0 ? `${n} Coachings` : null]
    .filter((x): x is NonNullable<typeof x> => !!x)
    .join(' · ')
}

export const memberViewConfig: ViewConfig<Member> = {
  rowKey: (m) => m.userId,
  title: (m) => memberDisplayName(m),
  emptyText: "Keine Mitglieder",

  cards: true,
  sortKeys: [
    { key: 'name', label: "Name", accessor: (m) => memberDisplayName(m) },
    { key: 'email', label: "E-Mail", accessor: (m) => m.email ?? '' },
  ],
  searchFields: (m) => [memberDisplayName(m), m.email],
  filterCategories: [
    {
      id: 'rolle',
      label: "Rolle",
      catalog: () => [
        { key: ROLLE_KEY.owner, label: "Owner" },
        { key: ROLLE_KEY.coach, label: "Coach" },
      ],
      accessor: rolleOf,
    },
    {
      id: 'status',
      label: "Status",
      catalog: () => [
        { key: STATUS_KEY.aktiv, label: "Aktiv" },
        { key: STATUS_KEY.ausstehend, label: "Ausstehend" },
      ],
      accessor: statusOf,
    },
  ],
  excludedFor: (filter) => excludedByCategoryFromMemberFilter(filter as MemberViewFilter),
}

export function memberViewConfigWithCoachings(coachings: readonly DashboardCoaching[]): ViewConfig<Member> {
  const counts = coachingCountsByCoachId(coachings)
  return {
    ...memberViewConfig,
    properties: [{ id: 'meta', value: (m: Member) => memberMetaWithCoachings(m, counts) }],
  }
}
